import { Component, ElementRef, Input, OnDestroy, OnInit } from "@angular/core";
import { Connection } from "@common/ADAPT.Common.Model/organisation/connection";
import { Role } from "@common/ADAPT.Common.Model/organisation/role";
import { RoleConnection } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { RoleLocation } from "@common/ADAPT.Common.Model/organisation/role-location";
import { RoleTypeCode } from "@common/ADAPT.Common.Model/organisation/role-type-code";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { IBreezeEntity } from "@common/lib/data/breeze-entity.interface";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { ArrayUtilities } from "@common/lib/utilities/array-utilities";
import { SortUtilities } from "@common/lib/utilities/sort-utilities";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { CommonIntegratedArchitectureFrameworkAuthService } from "@org-common/lib/architecture/common-integrated-architecture-framework-auth.service";
import { IntegratedArchitectureFrameworkQueryUtilities } from "@org-common/lib/architecture/integrated-architecture-framework-query-utilities";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { AuthorisationNotificationService } from "@org-common/lib/authorisation/authorisation-notification.service";
import { DirectorySharedService } from "@org-common/lib/directory-shared/directory-shared.service";
import { EditTeamRolesDialogComponent } from "@org-common/lib/team-roles/edit-team-roles-dialog/edit-team-roles-dialog.component";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { CommonTeamsAuthService } from "@org-common/lib/teams/common-teams-auth.service";
import { defer, lastValueFrom, switchMap } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { Options as SortablejsOptions, SortableEvent } from "sortablejs";

@Component({
    selector: "adapt-team-roles",
    templateUrl: "./team-roles.component.html",
    styleUrl: "./team-roles.component.scss",
    standalone: false,
})
export class TeamRolesComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input({ required: true }) public team!: Team;
    @Input() public nested = false;
    @Input() public isEditing = false;

    public canEdit = false;
    public groupByPerson = false;
    public hidePeople = false;
    private firstUpdate = true;

    private roles: Role[] = [];
    public teamRoleLocations: RoleLocation[] = [];
    public activeConnections = new Map<number, Connection>();
    public teamInputRoleConnections = new Map<number, RoleConnection>();
    public activeConnectionsForRole = new Map<Role, Set<Connection>>();
    public rolesForActiveConnections = new Map<Connection, Set<Role>>();

    public personDragging?: Connection;

    private sharedSortableJsOptions: SortablejsOptions = {
        // Mandatory for a good performance when using lots of shared lists.
        animation: 0,
        forceFallback: true,

        scroll: true,
        bubbleScroll: true,
        scrollSensitivity: 100,
        scrollSpeed: 50,

        sort: false,
    };

    public peopleListOptions: SortablejsOptions = {
        ...this.sharedSortableJsOptions,
        group: {
            name: "people-list",
            pull: "clone",
            put: false,
        },
        handle: ".card-person",
        revertOnSpill: true,
        onStart: (e) => {
            if (e.item) {
                const { connection } = this.getDraggedConnectionToRole(e.item);
                this.personDragging = connection;
            }
        },
        onSpill: () => this.personDragging = undefined,
        onEnd: this.createRoleConnection,
    };

    public roleListOptions: SortablejsOptions = {
        ...this.sharedSortableJsOptions,
        group: {
            name: "people-list",
            pull: false,
            put: (to, _, dragEl, __) => {
                const { connection, role } = this.getDraggedConnectionToRole(dragEl, to.el);
                if (!connection || !role || role.extensions.isSystemAllocatedRole()) {
                    return false;
                }

                // cannot drop connection into group with that connection already
                const roleConnections = this.activeConnectionsForRole.get(role);
                return !roleConnections?.has(connection);
            },
        },
        filter: () => true,
        preventOnFilter: true,
    };

    public roleOptions: SortablejsOptions = {
        forceFallback: true,
        animation: 150,
        draggable: ".card-role",
        handle: ".card-role .dx-list-reorder-handle",
        onEnd: this.reorderRoles,
    };

    private readonly archData = new IntegratedArchitectureFrameworkQueryUtilities(this.commonDataService);
    private readonly sortConnectionsByFullNameFunction = SortUtilities.getSortByFieldFunction<Connection>("person.fullName");

    public constructor(
        protected elementRef: ElementRef,
        private commonDataService: CommonDataService,
        private commonDialogService: AdaptCommonDialogService,
        private commonTeamsService: CommonTeamsService,
        private commonTeamsAuthService: CommonTeamsAuthService,
        private authorisationService: AuthorisationService,
        private directorySharedService: DirectorySharedService,
        authorisationNotificationService: AuthorisationNotificationService,
    ) {
        super(elementRef);

        authorisationNotificationService.authorisationChanged$.pipe(
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        ).subscribe();
    }

    public get hasRoles() {
        return this.roles.length > 0;
    }

    public async ngOnInit() {
        await this.updateData();
    }

    @Autobind
    public edit() {
        return this.commonDialogService.open(EditTeamRolesDialogComponent, this.team).pipe(
            switchMap(() => this.updateData()),
            this.takeUntilDestroyed(),
        );
    }

    @Autobind
    public async unpinRole(roleLocation: RoleLocation) {
        ArrayUtilities.removeElementFromArray(roleLocation, this.teamRoleLocations);
        SortUtilities.updateIntegerSortedArrayAfterItemRemoval(this.teamRoleLocations, "ordinal", roleLocation.ordinal);

        await lastValueFrom(this.commonDataService.remove(roleLocation));
        await lastValueFrom(this.saveEntitiesAndHandleSaveFailure(this.teamRoleLocations.concat(roleLocation)));
    }

    @Autobind
    public togglePinnedConnection(roleConnection: RoleConnection) {
        return defer(async () => this.commonTeamsService.removeTeamMember(roleConnection, true)).pipe(
            switchMap(() => this.saveEntitiesAndHandleSaveFailure([roleConnection!])),
        );
    }

    public async onRolesAdded(roles: Role[]) {
        await this.updateData();

        // highlight the newly added roles
        setTimeout(() => {
            for (const role of roles) {
                const element = this.elementRef.nativeElement?.querySelector(`[data-role-id='${role.roleId}']`);
                if (element) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                    element.classList.add("highlight");
                    setTimeout(() => element.classList.remove("highlight"), 2500);
                }
            }
        });
    }

    public async onRoleConnectionsAdded(connections: Connection[]) {
        await this.updateData();

        // highlight the newly added roles
        setTimeout(() => {
            for (const connection of connections) {
                const element = this.elementRef.nativeElement?.querySelector(`[data-connection-id='${connection.connectionId}']`);
                if (element) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                    });
                    element.classList.add("highlight");
                    setTimeout(() => element.classList.remove("highlight"), 2500);
                }
            }
        });
    }

    public async updateData() {
        this.personDragging = undefined;

        const hasAnyRoleEdit = await this.authorisationService.promiseToGetHasAccess(CommonIntegratedArchitectureFrameworkAuthService.EditAnyRole);
        const hasManageAccess = await this.directorySharedService.promiseToVerifyAccessToManageAccess();
        const hasTeamConfigure = this.commonTeamsAuthService.currentPersonCanEditTeam(this.team);
        this.canEdit = hasTeamConfigure || (this.commonTeamsAuthService.currentPersonCanReadTeam(this.team) && (hasAnyRoleEdit || hasManageAccess));

        // fetch team roles
        const { teamRoleLocations, roles } = await lastValueFrom(this.directorySharedService.getTeamRoleLocations(this.team));
        this.teamRoleLocations = teamRoleLocations;
        this.roles = roles;
        const teamRoleIds = this.teamRoleLocations.map((rl) => rl.roleId);

        // map so that we can keep the role order when switching to person grouping
        // TODO: do we want this?
        const roleOrdinalMap = new Map(this.teamRoleLocations.map((rl) => [rl.roleId, rl.ordinal]));

        // collapse the people if there are more than 4 roles
        if (this.firstUpdate && !this.isEditing) {
            this.hidePeople = this.teamRoleLocations.length > 4;
            this.firstUpdate = false;
        }

        const teamRoleConnections = await this.commonTeamsService.promiseToGetTeamMemberRoleConnections(this.team, true, true);
        const [teamMembers, teamInputs] = ArrayUtilities.partition(teamRoleConnections, (rc) => rc.role?.roleType?.code !== RoleTypeCode.TeamInput);

        const mapToSortedConnections = (input: RoleConnection[]) => input
            .map((rc) => rc.connection)
            .sort(this.sortConnectionsByFullNameFunction)
            .map((c) => [c.connectionId, c] as [number, Connection]);

        // add all real team members first
        this.activeConnections = new Map(mapToSortedConnections(teamMembers)
            .concat(mapToSortedConnections(teamInputs)));

        // track which connections are external inputs, so we can highlight them differently
        this.teamInputRoleConnections = new Map(teamInputs.map((rc) => [rc.connectionId, rc]));

        this.activeConnectionsForRole.clear();
        for (const teamRoleLocation of this.teamRoleLocations) {
            const roleConnections = await lastValueFrom(this.archData.getRoleConnectionsForRoleId(teamRoleLocation.roleId, true));
            const activeConnectionsForRole = roleConnections
                .filter((rc) => this.activeConnections.has(rc.connectionId))
                .map((rc) => rc.connection)
                .sort(this.sortConnectionsByFullNameFunction);
            this.activeConnectionsForRole.set(teamRoleLocation.role, new Set(activeConnectionsForRole));
        }

        this.rolesForActiveConnections.clear();
        for (const connection of this.activeConnections.values()) {
            const roleConnections = await lastValueFrom(this.archData.getRoleConnectionsForPersonId(connection.personId, true));
            const rolesForActiveConnection = roleConnections
                .filter((rc) => teamRoleIds.includes(rc.roleId))
                .map((rc) => rc.role)
                // TODO: sorted by the roleLocation ordinals right now... do we want this?
                //  could instead just be alphabetical.
                .sort(SortUtilities.getSortByFieldFunction<Role>((r) => roleOrdinalMap.get(r.roleId)));
            this.rolesForActiveConnections.set(connection, new Set(rolesForActiveConnection));
        }
    }

    @Autobind
    private async reorderRoles(e: SortableEvent) {
        const role = this.getDraggedRole(e.item);
        if (!role || e.oldDraggableIndex === undefined || e.newDraggableIndex === undefined) {
            return;
        }

        SortUtilities.reorderItemInIntegerSortedArray(this.teamRoleLocations, "ordinal", e.oldDraggableIndex, e.newDraggableIndex);
        this.saveEntitiesAndHandleSaveFailure(this.teamRoleLocations).subscribe();
    }

    @Autobind
    private async createRoleConnection(e: SortableEvent) {
        this.personDragging = undefined;

        const { connection, role } = this.getDraggedConnectionToRole(e.item, e.to);
        if (!connection || !role) {
            return;
        }

        // hack to workaround events being lost.
        // this is because you're actually dragging the real element,
        // the clone gets put in the place of the original. (doesn't make much sense to be honest...)
        // https://github.com/SortableJS/Sortable/issues/1515
        e.clone.replaceWith(e.item);

        const roleConnection = await lastValueFrom(this.archData.createRoleConnection({ connection, role }));

        this.saveEntitiesAndHandleSaveFailure([roleConnection]).subscribe();
    }

    private getDraggedConnectionToRole(draggedElement: HTMLElement, targetElement?: HTMLElement) {
        const dragConnectionId= Number(draggedElement.dataset.connectionId);
        const connection = this.activeConnections.get(dragConnectionId);
        const role = this.getDraggedRole(targetElement);

        return { connection, role };
    }

    private getDraggedRole(element?: HTMLElement) {
        const dropRoleId = element
            ? Number(element.dataset.roleId)
            : undefined;
        return dropRoleId
            ? this.roles.find((r) => r.roleId === dropRoleId)
            : undefined;
    }

    private saveEntitiesAndHandleSaveFailure(entities: IBreezeEntity[]) {
        return this.commonDataService.saveEntities(entities).pipe(
            catchError(() => this.commonDialogService.handleSaveFailure(entities)),
            // cannot use switchMap here as cancelling the save fail dialog won't continue the observable
            finalize(() => this.updateData()),
            this.takeUntilDestroyed(),
        );
    }
}
